import {
  BillingLineKind,
  BillingLineSide,
  BillingState,
  InvoiceLineItemType,
  RateType,
  RejectedReason,
} from '@treadinc/horizon-api-spec';
import { groupBy, isString, uniq } from 'lodash-es';

import { BillingLine } from '~hooks/useBillingLines/models';
import { toDecimal } from '~utils/decimal';
import isEnumValue from '~utils/isEnumValue';

interface SideCharge {
  hasUnsettledLines: boolean;
  // Every line on the side is rejected: `total` is then 0 and `rejectedTotal` holds what
  // the rejected lines would have billed
  isPercentRate: boolean;
  isRejected: boolean;
  isSettlementApproved: boolean;
  rate: number | null;
  rateType: string | null;
  rejectedReason: RejectedReason | null;
  rejectedTotal: number;
  settlementNumbers: string[];
  total: number;
  wasRate: number | null;
}

export interface LoadCharges {
  lines: BillingLine[];
  payable: SideCharge | null;
  receivable: SideCharge | null;
}

export interface ChargeRow {
  hasUnsettledLines: boolean;
  isPercentRate: boolean;
  isRejected: boolean;
  isRolledUp: boolean;
  isSettlementApproved: boolean;
  key: string;
  lines: BillingLine[];
  name: string;
  quantity: number | null;
  rate: number | null;
  rateType: string | null;
  rejectedReason: RejectedReason | null;
  settlementNumbers: string[];
  side: BillingLineSide;
  total: number;
  wasRate: number | null;
  wasTotalAmount: number | null;
}

interface LinesSummary {
  quantity: number | null;
  rate: number | null;
  rateType: string | null;
  total: number;
}

interface JobSideCharges {
  addOnsTotal: number;
  freight: SideCharge | null;
  hasLines: boolean;
  isRejected: boolean;
  perLoadFreight: LinesSummary | null;
  perLoadMaterial: LinesSummary | null;
  rejectedTotal: number;
  settlement: SideCharge | null;
  total: number;
}

export interface JobCharges {
  addOns: ChargeRow[];
  jobLines: ChargeRow[];
  lines: BillingLine[];
  loads: Map<string, LoadCharges>;
  payable: JobSideCharges;
  receivable: JobSideCharges;
}

export const sumAllTotals = (lines: BillingLine[]) =>
  lines.reduce((sum, line) => sum.plus(toDecimal(line.totalAmount)), toDecimal(0));

const sumTotals = (lines: BillingLine[]) =>
  sumAllTotals(lines.filter((line) => !line.isRejected));

const isEveryLineRejected = (lines: BillingLine[]) =>
  lines.length > 0 && lines.every((line) => line.isRejected);

// A load with no charges read yet is never hidden.
export const isLoadAllRejected = (charges: LoadCharges | undefined) =>
  Boolean(charges?.lines.length) &&
  Boolean(charges?.lines.every((line) => line.isRejected));

// The per-load lines of an add-on are named "<add-on name> - Load <n>". The add-on's own
// name is not on the charge (it carries only the add-on id), so the suffix comes off.
const PER_LOAD_SUFFIX = /\s+-\s+Load\s+\S+$/;
export const stripPerLoadSuffix = (name: string) => name.replace(PER_LOAD_SUFFIX, '');

export function getLineSettlementNumber(line: BillingLine) {
  const { settlement } = line;

  if (!settlement) {
    return null;
  }

  const sideNumber =
    line.side === BillingLineSide.RECEIVABLE
      ? settlement.arSettlementId
      : settlement.apSettlementId;

  return sideNumber || settlement.settlementId;
}

export function getLinesSettlementNumbers(lines: BillingLine[]) {
  return uniq(lines.map(getLineSettlementNumber).filter(isString));
}

const isApprovedSettlementByBillingState = {
  [BillingState.APPROVED]: true,
  [BillingState.DRAFT]: false,
  [BillingState.NOT_APPLIED]: false,
  [BillingState.PAID]: true,
  [BillingState.REJECTED]: false,
  [BillingState.UNSETTLED]: false,
} satisfies Record<BillingState, boolean>;

export type SettlementBlock = 'approved' | 'draft' | 'paid';

const settlementBlockByBillingState = {
  [BillingState.APPROVED]: 'approved',
  [BillingState.DRAFT]: 'draft',
  [BillingState.NOT_APPLIED]: null,
  [BillingState.PAID]: 'paid',
  [BillingState.REJECTED]: null,
  [BillingState.UNSETTLED]: null,
} satisfies Record<BillingState, SettlementBlock | null>;

export function getSettlementBlock(lines: BillingLine[]) {
  const blocks = new Set(
    lines.map(({ billingState }) =>
      billingState !== null && isEnumValue(BillingState, billingState)
        ? settlementBlockByBillingState[billingState]
        : null,
    ),
  );
  const block: SettlementBlock =
    (['paid', 'approved'] as const).find((candidate) => blocks.has(candidate)) ?? 'draft';
  const settlements = uniq(lines.map(getLineSettlementNumber).filter(isString)).join(
    ', ',
  );

  return { block, settlements };
}

export const areLinesOnApprovedSettlements = (lines: BillingLine[]) =>
  lines.length > 0 &&
  lines.every(
    ({ billingState }) =>
      billingState !== null &&
      isEnumValue(BillingState, billingState) &&
      isApprovedSettlementByBillingState[billingState],
  );

const isMaterialLine = (line: BillingLine) =>
  line.invoiceLineItem?.lineItemType === InvoiceLineItemType.MATERIAL_RATE_LINE_ITEM;

function getSharedRate(lines: BillingLine[]) {
  const rates = uniq(lines.map((line) => line.rate));
  const rateTypes = uniq(lines.map((line) => line.rateType));
  const sharesOneRate = rates.length === 1 && rateTypes.length === 1;

  return {
    rate: sharesOneRate ? rates[0] : null,
    rateType: sharesOneRate ? rateTypes[0] : null,
  };
}

function summarizeSide(lines: BillingLine[]): SideCharge | null {
  if (lines.length === 0) {
    return null;
  }

  const isRejected = isEveryLineRejected(lines);
  const countedLines = isRejected ? lines : lines.filter((line) => !line.isRejected);
  const freightLines = countedLines.filter((line) => !isMaterialLine(line));
  const { rate, rateType } = getSharedRate(
    freightLines.length > 0 ? freightLines : countedLines,
  );

  return {
    hasUnsettledLines: lines.some(
      (line) => !line.isRejected && getLineSettlementNumber(line) === null,
    ),
    isPercentRate: countedLines.every((line) => line.isPercentRate),
    isRejected,
    isSettlementApproved: areLinesOnApprovedSettlements(countedLines),
    rate,
    rateType,
    rejectedReason: isRejected ? lines[0].rejectedReason : null,
    rejectedTotal: sumAllTotals(lines.filter((line) => line.isRejected)).toNumber(),
    settlementNumbers: getLinesSettlementNumbers(countedLines),
    total: sumTotals(lines).toNumber(),
    wasRate: lines.length === 1 ? lines[0].wasRate : null,
  };
}

function summarizeQuantity(lines: BillingLine[]) {
  const quantities = lines.map((line) => line.quantity);

  if (quantities.some((quantity) => quantity === null)) {
    return null;
  }

  return lines
    .reduce((sum, line) => sum.plus(toDecimal(line.quantity)), toDecimal(0))
    .toNumber();
}

function summarizeLines(lines: BillingLine[]): LinesSummary | null {
  if (lines.length === 0) {
    return null;
  }

  return {
    ...getSharedRate(lines),
    quantity: summarizeQuantity(lines),
    total: sumAllTotals(lines).toNumber(),
  };
}

function makeChargeRow(
  key: string,
  lines: BillingLine[],
  isRolledUp: boolean,
): ChargeRow {
  const [firstLine] = lines;
  const summary = summarizeSide(lines);
  const name = firstLine.name ?? '';

  return {
    hasUnsettledLines: summary?.hasUnsettledLines ?? false,
    isPercentRate: summary?.isPercentRate ?? false,
    isRejected: summary?.isRejected ?? false,
    isRolledUp,
    isSettlementApproved: summary?.isSettlementApproved ?? false,
    key,
    lines,
    name: isRolledUp ? stripPerLoadSuffix(name) : name,
    quantity: summarizeQuantity(
      summary?.isRejected ? lines : lines.filter((line) => !line.isRejected),
    ),
    rate: summary?.rate ?? null,
    rateType: summary?.rateType ?? null,
    rejectedReason: summary?.rejectedReason ?? null,
    settlementNumbers: summary?.settlementNumbers ?? [],
    side: firstLine.side,
    total: summary?.isRejected ? summary.rejectedTotal : (summary?.total ?? 0),
    wasRate: summary?.wasRate ?? null,
    wasTotalAmount: lines.length === 1 ? lines[0].wasTotalAmount : null,
  };
}

// An add-on is the same add-on across loads when it carries the same add-on id, and
// otherwise when name and rate type match
const getAddOnIdentity = (line: BillingLine) =>
  line.addOnCharge?.addOnId ?? `${stripPerLoadSuffix(line.name ?? '')}:${line.rateType}`;

function makeAddOnRows(addOnLines: BillingLine[]) {
  const linesByAddOnAndSide = new Map<string, BillingLine[]>();

  addOnLines.forEach((line) => {
    const key = `${line.side}:${getAddOnIdentity(line)}`;
    linesByAddOnAndSide.set(key, [...(linesByAddOnAndSide.get(key) ?? []), line]);
  });

  return [...linesByAddOnAndSide.entries()].flatMap(([key, lines]) => {
    const perLoadLines = lines.filter((line) => line.scope.load);
    const jobLines = lines.filter((line) => !line.scope.load);

    return [
      ...(perLoadLines.length > 0 ? [makeChargeRow(key, perLoadLines, true)] : []),
      ...jobLines.map((line) => makeChargeRow(`${key}:${line.id}`, [line], false)),
    ];
  });
}

function summarizeJobSide(lines: BillingLine[], side: BillingLineSide): JobSideCharges {
  const sideLines = lines.filter((line) => line.side === side);
  const freightLines = sideLines.filter(
    (line) => line.kind !== BillingLineKind.ADD_ON_CHARGE,
  );
  const addOnLines = sideLines.filter(
    (line) => line.kind === BillingLineKind.ADD_ON_CHARGE,
  );
  const countedPerLoadLines = freightLines.filter(
    (line) => line.scope.load && !line.isRejected,
  );

  return {
    addOnsTotal: sumTotals(addOnLines).toNumber(),
    freight: summarizeSide(freightLines),
    hasLines: sideLines.length > 0,
    isRejected: isEveryLineRejected(sideLines),
    perLoadFreight: summarizeLines(
      countedPerLoadLines.filter((line) => !isMaterialLine(line)),
    ),
    perLoadMaterial: summarizeLines(countedPerLoadLines.filter(isMaterialLine)),
    rejectedTotal: sumAllTotals(sideLines.filter((line) => line.isRejected)).toNumber(),
    settlement: summarizeSide(sideLines),
    total: sumTotals(sideLines).toNumber(),
  };
}

function summarizeLoadCharges(
  freightLines: BillingLine[],
  lines: BillingLine[],
): LoadCharges {
  return {
    lines,
    payable: summarizeSide(
      freightLines.filter((line) => line.side === BillingLineSide.PAYABLE),
    ),
    receivable: summarizeSide(
      freightLines.filter((line) => line.side === BillingLineSide.RECEIVABLE),
    ),
  };
}

export function groupBillingLinesByJob(lines: BillingLine[]) {
  const linesByJobId = new Map<string, BillingLine[]>();

  lines.forEach((line) => {
    const jobId = line.scope.job?.id;

    if (jobId) {
      linesByJobId.set(jobId, [...(linesByJobId.get(jobId) ?? []), line]);
    }
  });

  return new Map(
    [...linesByJobId.entries()].map(([jobId, jobLines]): [string, JobCharges] => {
      const freightLines = jobLines.filter(
        (line) => line.kind !== BillingLineKind.ADD_ON_CHARGE,
      );
      const addOnLines = jobLines.filter(
        (line) => line.kind === BillingLineKind.ADD_ON_CHARGE,
      );

      const freightLinesByLoadId = new Map<string, BillingLine[]>();
      const freightLinesOfJob: BillingLine[] = [];
      const linesByLoadId = new Map<string, BillingLine[]>();

      jobLines.forEach((line) => {
        const loadId = line.scope.load?.id;

        if (loadId) {
          linesByLoadId.set(loadId, [...(linesByLoadId.get(loadId) ?? []), line]);
        }
      });

      freightLines.forEach((line) => {
        const loadId = line.scope.load?.id;

        if (!loadId) {
          freightLinesOfJob.push(line);

          return;
        }

        freightLinesByLoadId.set(loadId, [
          ...(freightLinesByLoadId.get(loadId) ?? []),
          line,
        ]);
      });

      return [
        jobId,
        {
          addOns: makeAddOnRows(addOnLines),
          jobLines: freightLinesOfJob.map((line) =>
            makeChargeRow(`${line.side}:${line.id}`, [line], false),
          ),
          lines: jobLines,
          loads: new Map(
            [...linesByLoadId.entries()].map(([loadId, loadLines]) => [
              loadId,
              summarizeLoadCharges(freightLinesByLoadId.get(loadId) ?? [], loadLines),
            ]),
          ),
          payable: summarizeJobSide(jobLines, BillingLineSide.PAYABLE),
          receivable: summarizeJobSide(jobLines, BillingLineSide.RECEIVABLE),
        },
      ];
    }),
  );
}

export const isSideBilledPerLoad = (charges: JobCharges, side: BillingLineSide) =>
  charges[side].hasLines && !charges.jobLines.some((row) => row.side === side);

const isRateTypeBilledByUnit = {
  [RateType.RATE_COMMISSION]: true,
  [RateType.RATE_FLAT_COMMISSION]: true,
  [RateType.RATE_FLAT_RATE]: false,
  [RateType.RATE_PER_BUSHEL]: true,
  [RateType.RATE_PER_DAY]: false,
  [RateType.RATE_PER_EACH]: true,
  [RateType.RATE_PER_HOUR]: false,
  [RateType.RATE_PER_LOAD]: true,
  [RateType.RATE_PER_POUND]: true,
  [RateType.RATE_PER_TON]: true,
  [RateType.RATE_PER_TONNE]: true,
  [RateType.RATE_PER_YARD]: true,
} satisfies Record<RateType, boolean>;

const isLineBilledByUnit = ({ rateType }: BillingLine) =>
  rateType !== null &&
  isEnumValue(RateType, rateType) &&
  isRateTypeBilledByUnit[rateType];

export const getTimeBasedLines = (charges: JobCharges) =>
  charges.lines.filter((line) => !isSideBilledPerLoad(charges, line.side));

const getLoadAndSideKey = (line: BillingLine) =>
  `${line.scope.load?.id ?? ''}:${line.side}`;

export const getLinesSettledByUnit = (lines: BillingLine[]) => {
  const linesByLoadAndSide = groupBy(lines, getLoadAndSideKey);

  return lines.filter((line) => {
    const chargeLines = linesByLoadAndSide[getLoadAndSideKey(line)].filter(
      (sideLine) => sideLine.kind !== BillingLineKind.ADD_ON_CHARGE,
    );

    return chargeLines.length > 0 && chargeLines.every(isLineBilledByUnit);
  });
};

export const getPerLoadLinesSettledByUnit = (charges: JobCharges) =>
  getLinesSettledByUnit(
    charges.lines.filter((line) => isSideBilledPerLoad(charges, line.side)),
  );
